import { useEffect, useRef } from "react";

const AT_TOP = 12; // px of scroll still counted as the top of the page
const SCROLL_STEP = 8; // px of travel before a scroll direction counts
const REVEAL_EDGE = 24; // px from the top of the window that summons a hidden bar

// Tucks a fixed top bar out of sight once the page scrolls down, as in
// He-Website-Source: scrolling back up brings it back, and so does moving the mouse
// up to the top edge of the window. Pair the header with the `he-topbar-shell` class.
//
// `scrollRef` points at the element that scrolls; leave it out when the window does.
// While `pinned` is true (a menu hanging off the bar is open) the bar stays in view.
export default function useAutoHideHeader(headerRef, { scrollRef, pinned = false } = {}) {
  const pinnedRef = useRef(pinned);
  pinnedRef.current = pinned;
  const applyRef = useRef(null);

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const scroller = scrollRef ? scrollRef.current : window;
    if (!scroller) return;
    const scrollTop = () => Math.max(0, scrollRef ? scroller.scrollTop : window.scrollY);

    let frame = 0;
    let scrollAnchor = scrollTop();
    let scrollHidden = false;
    let pointerNear = false;

    // The phone menu, the bar's dropdowns and keyboard focus also hold it in view
    const isPinned = () =>
      pinnedRef.current ||
      !!header.querySelector(".is-open") ||
      !!header.querySelector(":focus-visible") ||
      !!document.querySelector(".he-topbar-menu-box");

    const apply = () => {
      const atTop = scrollTop() <= AT_TOP;
      const hidden = !atTop && scrollHidden && !pointerNear && !isPinned();
      header.classList.toggle("he-nav-at-top", atTop);
      header.classList.toggle("he-nav-raised", !atTop);
      header.classList.toggle("he-nav-hidden", hidden);
    };
    applyRef.current = apply;

    const update = () => {
      frame = 0;
      const y = scrollTop();
      if (y <= AT_TOP) {
        scrollHidden = false;
        scrollAnchor = y;
      } else {
        const delta = y - scrollAnchor;
        if (Math.abs(delta) >= SCROLL_STEP) {
          scrollHidden = delta > 0;
          scrollAnchor = y;
        }
      }
      apply();
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(update); };

    // A hidden bar answers to the top edge; once shown it stays while the pointer is
    // over it or one of its dropdowns
    const onPointerMove = (e) => {
      if (e.pointerType && e.pointerType !== "mouse") return;
      const shown = !header.classList.contains("he-nav-hidden");
      const near = shown
        ? e.clientY <= header.getBoundingClientRect().bottom + 12 ||
          !!e.target.closest?.(".he-topbar-menu-box")
        : e.clientY <= REVEAL_EDGE;
      if (near !== pointerNear) {
        pointerNear = near;
        apply();
      }
    };
    const onPointerLeave = () => {
      if (!pointerNear) return;
      pointerNear = false;
      apply();
    };

    scroller.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("pointermove", onPointerMove, { passive: true });
    document.documentElement.addEventListener("mouseleave", onPointerLeave);
    header.addEventListener("focusin", apply);
    header.addEventListener("focusout", apply);
    update();
    return () => {
      cancelAnimationFrame(frame);
      applyRef.current = null;
      scroller.removeEventListener("scroll", onScroll);
      document.removeEventListener("pointermove", onPointerMove);
      document.documentElement.removeEventListener("mouseleave", onPointerLeave);
      header.removeEventListener("focusin", apply);
      header.removeEventListener("focusout", apply);
    };
  }, [headerRef, scrollRef]);

  // Closing a pinned menu lets a scrolled-away bar tuck back out of sight
  useEffect(() => {
    applyRef.current?.();
  }, [pinned]);
}
